import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  useTheme,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  Star as StarIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { PageLayout } from '../../components/footer/PageLayout';
import { ContentSection, FeatureCard } from '../../components/footer/ContentSection';
import { CTASection } from '../../components/footer/CTASection';

export const HowItWorksPage: React.FC = () => {
  const theme = useTheme();

  const patientSteps = [
    {
      icon: <PersonAddIcon sx={{ fontSize: 40 }} />,
      title: "Create Your Account",
      description: "Sign up as a patient and create your profile. Tell us about your healthcare needs and preferences.",
    },
    {
      icon: <SearchIcon sx={{ fontSize: 40 }} />,
      title: "Search & Browse",
      description: "Use our search tools to find caregivers in your area. Filter by specialty, availability, and ratings.",
    },
    {
      icon: <ScheduleIcon sx={{ fontSize: 40 }} />,
      title: "Book Your Appointment",
      description: "Choose a caregiver and schedule your appointment. Select a time that works for your schedule.",
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 40 }} />,
      title: "Secure Payment",
      description: "Pay securely through our platform. We accept insurance and offer flexible payment options.",
    },
    {
      icon: <StarIcon sx={{ fontSize: 40 }} />,
      title: "Receive Care",
      description: "Meet your caregiver and receive quality healthcare services in the comfort of your home.",
    },
    {
      icon: <SupportIcon sx={{ fontSize: 40 }} />,
      title: "Rate & Review",
      description: "Share your experience to help other patients and improve our platform for everyone.",
    },
  ];

  const caregiverSteps = [
    {
      step: "1",
      title: "Apply to Join",
      description: "Submit your application with credentials, licenses, and background check information.",
    },
    {
      step: "2",
      title: "Get Verified",
      description: "Our team reviews your qualifications and conducts background checks to ensure quality.",
    },
    {
      step: "3",
      title: "Create Your Profile",
      description: "Build a compelling profile showcasing your experience, specialties, and availability.",
    },
    {
      step: "4",
      title: "Start Earning",
      description: "Accept appointments from patients and begin providing quality care while earning competitive rates.",
    },
  ];

  const benefits = [
    {
      title: "For Patients",
      items: [
        "Access to verified, qualified caregivers",
        "Flexible scheduling options",
        "Transparent pricing and insurance coverage",
        "Secure payment processing",
        "Real patient reviews and ratings",
        "24/7 customer support",
      ],
    },
    {
      title: "For Caregivers",
      items: [
        "Flexible work schedule",
        "Competitive earning potential",
        "Direct patient relationships",
        "Professional development resources",
        "Secure payment processing",
        "Marketing and profile support",
      ],
    },
  ];

  return (
    <PageLayout
      title="How Careva Works"
      subtitle="A simple, secure platform connecting patients with qualified caregivers for quality healthcare services."
    >
      {/* Patient Journey */}
      <ContentSection
        title="For Patients: Your Healthcare Journey"
        subtitle="Getting started is easy. Follow these simple steps to find and book quality healthcare services."
        backgroundColor={theme.palette.grey[50]}
      >
        <Grid container spacing={4}>
          {patientSteps.map((step, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <FeatureCard {...step} />
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* Caregiver Journey */}
      <ContentSection
        title="For Caregivers: Start Your Practice"
        subtitle="Join our network of healthcare professionals and start building your practice today."
      >
        <Grid container spacing={4}>
          {caregiverSteps.map((step, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {step.step}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    color: theme.palette.text.primary,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  {step.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* Benefits */}
      <ContentSection
        title="Why Choose Careva?"
        subtitle="Discover the benefits that make our platform the preferred choice for patients and caregivers alike."
        backgroundColor={theme.palette.grey[50]}
      >
        <Grid container spacing={6}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: theme.palette.primary.main,
                    mb: 3,
                  }}
                >
                  {benefit.title}
                </Typography>
                <Box component="ul" sx={{ pl: 0, listStyle: 'none' }}>
                  {benefit.items.map((item, itemIndex) => (
                    <Box component="li" key={itemIndex} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.success.main,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          mr: 2,
                          mt: 0.5,
                          flexShrink: 0,
                        }}
                      >
                        ✓
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.text.primary,
                          lineHeight: 1.6,
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* Safety & Trust */}
      <ContentSection
        title="Safety & Trust"
        subtitle="Your safety and privacy are our top priorities. Learn about our comprehensive safety measures."
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.success.light,
                  color: theme.palette.success.contrastText,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                🔒
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  color: theme.palette.text.primary,
                }}
              >
                Background Checks
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6,
                }}
              >
                All caregivers undergo comprehensive background checks and credential verification.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.info.light,
                  color: theme.palette.info.contrastText,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                🛡️
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  color: theme.palette.text.primary,
                }}
              >
                Insurance Coverage
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6,
                }}
              >
                All services are covered by professional liability insurance for your protection.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.warning.light,
                  color: theme.palette.warning.contrastText,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                🔐
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  color: theme.palette.text.primary,
                }}
              >
                HIPAA Compliant
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6,
                }}
              >
                Your health information is protected with enterprise-grade security and HIPAA compliance.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </ContentSection>

      {/* CTA Section */}
      <CTASection
        title="Ready to Get Started?"
        subtitle="Join thousands of patients and caregivers who trust Careva for quality healthcare services."
        primaryButton={{
          text: "Sign Up as Patient",
          href: "/auth?role=patient",
        }}
        secondaryButton={{
          text: "Become a Caregiver",
          href: "/auth?role=caregiver",
          variant: "outlined",
        }}
      />
    </PageLayout>
  );
};
